import {
  blueBase,
  BlueBaseColor,
  BrandBaseColors,
  DuneBaseColors,
  greenBase,
  GreenBaseColor,
  orangeBase,
  OrangeBaseColor,
  redBase,
  RedBaseColor,
  yellowBase,
  YellowBaseColor,
} from './baseColors';
import { getAlphaColor } from './color.utils';
import { profileColors } from './profileColors';

const baseColor = {
  ...OrangeBaseColor,
  ...RedBaseColor,
  ...YellowBaseColor,
  ...GreenBaseColor,
  ...BlueBaseColor,
  ...BrandBaseColors,
  ...DuneBaseColors,
};

export const GrayColors = {
  gray100: DuneBaseColors.dune0,
  gray150: DuneBaseColors.dune50,
  gray200: DuneBaseColors.dune100,
  gray250: DuneBaseColors.dune150,
  gray300: DuneBaseColors.dune200,
  gray400: DuneBaseColors.dune300,
  gray450: DuneBaseColors.dune350,
  gray500: DuneBaseColors.dune400,
  gray600: DuneBaseColors.dune450,
  gray700: DuneBaseColors.dune600,
  gray800: DuneBaseColors.dune700,
  gray900: DuneBaseColors.dune800,
  gray950: DuneBaseColors.dune950,
};

const ND = GrayColors.gray950;
const NL = GrayColors.gray100;

export const NDBaseColor = {
  ND100: ND,
  ND80: getAlphaColor(ND, 0.8),
  ND60: getAlphaColor(ND, 0.6),
  ND50: getAlphaColor(ND, 0.5),
  ND40: getAlphaColor(ND, 0.4),
  ND30: getAlphaColor(ND, 0.3),
  ND20: getAlphaColor(ND, 0.2),
  ND16: getAlphaColor(ND, 0.16),
  ND12: getAlphaColor(ND, 0.12),
  ND08: getAlphaColor(ND, 0.08),
  ND06: getAlphaColor(ND, 0.06),
  ND04: getAlphaColor(ND, 0.04),
  ND02: getAlphaColor(ND, 0.02),
  ND00: getAlphaColor(ND, 0),
};

export const NLBaseColor = {
  NL100: NL,
  NL80: getAlphaColor(NL, 0.8),
  NL60: getAlphaColor(NL, 0.6),
  NL40: getAlphaColor(NL, 0.4),
  NL20: getAlphaColor(NL, 0.2),
  NL16: getAlphaColor(NL, 0.16),
  NL08: getAlphaColor(NL, 0.08),
  NL04: getAlphaColor(NL, 0.04),
  NL02: getAlphaColor(NL, 0.02),
  NL00: getAlphaColor(NL, 0),
};

export const FLBaseColor = {
  FL100: NL,
  FL80: getAlphaColor(NL, 0.8),
  FL60: getAlphaColor(NL, 0.6),
  FL40: getAlphaColor(NL, 0.4),
  FL20: getAlphaColor(NL, 0.2),
  FL16: getAlphaColor(NL, 0.16),
  FL12: getAlphaColor(NL, 0.12),
  FL08: getAlphaColor(NL, 0.08),
  FL04: getAlphaColor(NL, 0.04),
  FL02: getAlphaColor(NL, 0.02),
  FL00: getAlphaColor(NL, 0),
};

export const FDBaseColor = {
  FD100: ND,
  FD80: getAlphaColor(ND, 0.8),
  FD60: getAlphaColor(ND, 0.6),
  FD40: getAlphaColor(ND, 0.4),
  FD20: getAlphaColor(ND, 0.2),
  FD16: getAlphaColor(ND, 0.16),
  FD12: getAlphaColor(ND, 0.12),
  FD08: getAlphaColor(ND, 0.08),
  FD04: getAlphaColor(ND, 0.04),
  FD02: getAlphaColor(ND, 0.02),
  FD00: getAlphaColor(ND, 0),
};

export const PrimaryColors = {
  primary300: GrayColors.gray700,
  primary200: GrayColors.gray800,
  primary100: GrayColors.gray900,
};

export const ErrorLightColor = {
  error_active: RedBaseColor.red800,
  error_hover: RedBaseColor.red700,
  error: RedBaseColor.red600,
  error_bg: getAlphaColor(RedBaseColor.red600, 0.4),
};

export const SentimentColors = {
  positive: GreenBaseColor.green500,
  neutral: GrayColors.gray500,
  negative: RedBaseColor.red600,
};

const semanticColors = {
  profile: [
    profileColors.profile0,
    profileColors.profile1,
    profileColors.profile2,
    profileColors.profile3,
    profileColors.profile4,
    profileColors.profile5,
    profileColors.profile6,
    profileColors.profile7,
    profileColors.profile8,
    profileColors.profile9,
    profileColors.profile10,
    profileColors.profile11,
  ],
  sentiment: {
    1: SentimentColors.positive,
    2: SentimentColors.neutral,
    3: SentimentColors.negative,
  },

  mark: '#e6e9ff',
  hover: GrayColors.gray150,
  selected: GrayColors.gray150,
  selected_hover: GrayColors.gray150,
  shadow: NDBaseColor.ND08,
  shadow_soft: NDBaseColor.ND04,
  info: YellowBaseColor.yellow500,
  alert: RedBaseColor.red600,
  yellow: (opacity: number) => getAlphaColor(yellowBase, opacity),
  orange: (opacity: number) => getAlphaColor(orangeBase, opacity),
  primaryAlpha: (opacity: number) => getAlphaColor(GrayColors.gray900, opacity),
  green: (opacity: number) => getAlphaColor(greenBase, opacity),
  red: (opacity: number) => getAlphaColor(redBase, opacity),
  blue: (opacity: number) => getAlphaColor(blueBase, opacity),

  // tailwind semantic colors
  background: DuneBaseColors.dune0,
  foreground: DuneBaseColors.dune900,
  card: DuneBaseColors.dune0,
  card_foreground: DuneBaseColors.dune950,
  popover: DuneBaseColors.dune0,
  popover_foreground: DuneBaseColors.dune950,
  primary: DuneBaseColors.dune900,
  primary_foreground: DuneBaseColors.dune50,
  secondary: DuneBaseColors.dune100,
  secondary_foreground: DuneBaseColors.dune900,
  muted: DuneBaseColors.dune100,
  muted_foreground: DuneBaseColors.dune600,
  accent: 'color-mix(in srgb, #1E1D1A 5%, transparent)',
  accent_foreground: DuneBaseColors.dune900,
  destructive: RedBaseColor.red600,
  destructive_foreground: RedBaseColor.red50,
  border: DuneBaseColors.dune200,
  input: DuneBaseColors.dune200,
  ring: DuneBaseColors.dune900,
  sidebar_background: DuneBaseColors.dune50,
  sidebar_foreground: DuneBaseColors.dune700,
  sidebar_primary: DuneBaseColors.dune900,
  sidebar_primary_foreground: DuneBaseColors.dune50,
  sidebar_accent: DuneBaseColors.dune200,
  sidebar_accent_foreground: DuneBaseColors.dune900,
  sidebar_border: DuneBaseColors.dune200,
  sidebar_ring: DuneBaseColors.dune400,
};

const colors = {
  ...baseColor,
  ...NDBaseColor,
  ...NLBaseColor,
  ...FLBaseColor,
  ...FDBaseColor,
  ...GrayColors,
  ...PrimaryColors,
  ...ErrorLightColor,
  ...semanticColors,
};

export default colors;

export type ColorTypes = typeof colors;
